import { EDocumentType, IPresignedUrlRequest, ISessionPresignedUrlRequest } from "../interfaces";
import { ALLOWED_FILE_EXTENSIONS, FILE_SIZE_CONSTANTS, ESessionDocumentType } from "../constants";
import { isEmpty, isDefined, isNullOrUndefined } from "./common";

export interface IFileValidationResult {
    isValid: boolean;
    error?: string;
}

/**
 * Gets allowed file extensions for a document type
 */
export function getAllowedExtensions(documentType: EDocumentType): string[] {
    return ALLOWED_FILE_EXTENSIONS[documentType] ?? ["jpg", "jpeg", "png", "pdf"];
}

/**
 * Validates a file extension against allowed extensions for a document type
 */
export function isValidFileExtension(
    fileExtension: string | undefined,
    documentType: EDocumentType
): boolean {
    if (isEmpty(fileExtension) || !isDefined(fileExtension)) {
        return false;
    }
    const allowedExtensions = getAllowedExtensions(documentType);
    return allowedExtensions.includes(fileExtension);
}

/**
 * Converts bytes to megabytes
 */
export function bytesToMB(bytes: number): number {
    return bytes / FILE_SIZE_CONSTANTS.BYTES_PER_MB;
}

/**
 * Converts megabytes to bytes
 */
export function mbToBytes(mb: number): number {
    return mb * FILE_SIZE_CONSTANTS.BYTES_PER_MB;
}

/**
 * Validates a file for upload
 */
export function validateFileForUpload(
    file: File,
    documentType: EDocumentType,
    maxSizeMB: number
): IFileValidationResult {
    // Check file size
    const fileSizeMB = bytesToMB(file.size);
    if (fileSizeMB > maxSizeMB) {
        return {
            isValid: false,
            error: `File size must be less than ${maxSizeMB}MB`,
        };
    }

    // Check file extension
    const fileExtension = getFileExtension(file.name);
    if (!isValidFileExtension(fileExtension, documentType)) {
        const allowedExtensions = getAllowedExtensions(documentType);
        return {
            isValid: false,
            error: `File must be one of: ${allowedExtensions.join(', ')}`,
        };
    }

    // Check file type
    if (!isImageFile(file) && !isPDFFile(file)) {
        return {
            isValid: false,
            error: "File must be an image or PDF",
        };
    }

    return { isValid: true };
}

/**
 * Gets file extension from file name
 */
export function getFileExtension(fileName: string): string | undefined {
    return fileName.split('.').pop()?.toLowerCase();
}

/**
 * Builds accept types object for react-dropzone
 */
export function buildAcceptTypes(allowedExtensions: string[]): Record<string, string[]> {
    const acceptTypes: Record<string, string[]> = {};
    const imageExtensions = allowedExtensions.filter(ext => ['jpg', 'jpeg', 'png'].includes(ext));
    const hasPDF = allowedExtensions.includes('pdf');

    if (imageExtensions.length > 0) {
        acceptTypes['image/*'] = imageExtensions.map(ext => `.${ext}`);
    }
    if (hasPDF) {
        acceptTypes['application/pdf'] = ['.pdf'];
    }

    return acceptTypes;
}

/**
 * Creates presigned URL request from file extension
 * Note: The AllowedFileExtension type is a union of arrays, but backend expects a single string
 * This function validates and returns a properly typed request
 */
export function createPresignedUrlRequest(
    documentType: EDocumentType,
    fileExtension: string | undefined
): IPresignedUrlRequest | null {
    if (!isValidFileExtension(fileExtension, documentType) || !isDefined(fileExtension)) {
        return null;
    }

    // The type system expects AllowedFileExtension which is a union of arrays
    // but the backend actually expects a single string extension
    // We validate it's in the allowed list and return it
    // This is a known type mismatch between frontend types and backend API
    return {
        documentType,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fileExtension: fileExtension as any, // Type mismatch: backend expects string, type system expects array union
    };
}

/**
 * Checks if a file is an image
 */
export function isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
}

/**
 * Checks if a file is a PDF
 */
export function isPDFFile(file: File): boolean {
    const fileExtension = getFileExtension(file.name);
    return file.type === 'application/pdf' || (isDefined(fileExtension) && fileExtension === 'pdf');
}

/**
 * Checks if a URL points to an image file based on its extension
 */
export function isImageURL(url: string): boolean {
    const fileExtension = getFileExtension(url);
    if (!isDefined(fileExtension)) {
        return false;
    }
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    return imageExtensions.includes(fileExtension);
}

/**
 * Checks if a URL points to a PDF file based on its extension
 */
export function isPDFURL(url: string): boolean {
    const fileExtension = getFileExtension(url);
    return isDefined(fileExtension) && fileExtension === 'pdf';
}

/**
 * Extracts the base URL from a presigned URL (everything before the query string)
 * @param presignedUrl - The full presigned URL with query parameters
 * @returns The base URL without query parameters, or the original URL if no query string exists
 * @example
 * getBaseUrlFromPresignedUrl("https://example.com/file.png?param=value")
 * // Returns: "https://example.com/file.png"
 */
export function getBaseUrlFromPresignedUrl(presignedUrl: string): string {
    if (isNullOrUndefined(presignedUrl) || isEmpty(presignedUrl)) {
        return "";
    }

    // Find the index of the first '?' character
    const queryIndex = presignedUrl.indexOf("?");

    // If no query string exists, return the original URL
    if (queryIndex === -1) {
        return presignedUrl;
    }

    // Return everything before the '?'
    return presignedUrl.substring(0, queryIndex);
}

const SESSION_DOCUMENT_ALLOWED_EXTENSIONS: Record<ESessionDocumentType, string[]> = {
    [ESessionDocumentType.SELFIE]: ["jpg", "jpeg", "png"],
    [ESessionDocumentType.TSHIRT]: ["jpg", "jpeg", "png"],
    [ESessionDocumentType.SHOES]: ["jpg", "jpeg", "png"],
    [ESessionDocumentType.KIT]: ["jpg", "jpeg", "png"],
    [ESessionDocumentType.BEFORE]: ["jpg", "jpeg", "png"],
    [ESessionDocumentType.AFTER]: ["jpg", "jpeg", "png"],
    [ESessionDocumentType.AFTER_SESSION_CLEANUP]: ["jpg", "jpeg", "png"],
    [ESessionDocumentType.CANCELLATION_PROOF]: ["jpg", "jpeg", "png", "pdf"],
};

export function isValidSessionDocumentExtension(
    fileExtension: string | undefined,
    documentType: ESessionDocumentType
): boolean {
    if (isEmpty(fileExtension) || !isDefined(fileExtension)) {
        return false;
    }
    const allowedExtensions = SESSION_DOCUMENT_ALLOWED_EXTENSIONS[documentType] ?? [];
    return allowedExtensions.includes(fileExtension.toLowerCase());
}

export function createSessionPresignedUrlRequest(
    documentType: ESessionDocumentType,
    fileExtension: string | undefined
): ISessionPresignedUrlRequest | null {
    if (!isValidSessionDocumentExtension(fileExtension, documentType) || !isDefined(fileExtension)) {
        return null;
    }

    return {
        documentType,
        fileExtension: fileExtension.toLowerCase(),
    };
}
